import { Button, Chip, Group, IconButton, Loader, Stack, Text, Title, toast, Tooltip } from '@common/ui'
import { IconChevronRight } from '@common/ui/icons'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import PayloadEditor from './PayloadEditor'
import { Language } from './LanguageSelect'
import { com, kotlin } from '@eidu/web'
import PermissionError from '../../PermissionError'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import ScrollFadeContainer from '../../../../components/layout/ScrollFadeContainer'
import PersistentTabs, { PersistentTab } from '../../../../components/layout/PersistentTabs'
import ResizableSplit from '../../../../components/layout/ResizableSplit'
import PageLayout from '../shared/PageLayout'
import useEntityEdit from '../shared/useEntityEdit'
import { KindEditPageProps } from '../KindPages'
import { getEntityLinkFromResourceKey, getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import { useModifyEntity } from '../../../../io/useEntityMutations'
import DraftFieldValue, { draftFieldValuesToFieldValues } from '../../../../domain/entity/DraftFieldValue'
import Link from '../../../../domain/entity/Link'
import computeLabel from '../shared/computeLabel'
import useUploadResource from '../../../../io/useUploadResource'
import useInitialPayload from './useInitialPayload'
import EntityWithLabelAndRelated from '../../../../domain/entity/EntityWithLabelAndRelated'
import RuntimeSelector from './RuntimeSelector'
import PayloadDocumentationButton from './PayloadDocumentation'
import useRuntimeMetadata from '../../../../io/useRuntimeMetadata'
import { AssetPreviewView, useLocalRuntimes } from './AssetPreviewView'
import LogPanel, { useLogEntries } from './LogPanel'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import { getEntityLinkFromRuntimeRef, getRuntimeRefFromEntity } from '../../../../io/entityRuntimeIdLink'
import { formatRuntimeRef, RuntimeRef } from '../../../../io/runtimeRef'
import Entity = com.eidu.entity.Entity
import EntityId = com.eidu.entity.EntityId
import entityIdFromString = com.eidu.entity.entityIdFromString
import KtMap = kotlin.collections.KtMap
import assetRuntimeLinkKey = com.eidu.entity.assetRuntimeLinkKey
import assetResourceLinkKey = com.eidu.entity.assetResourceLinkKey

type AssetEditTab = 'payload' | 'fields'

export type RenderPayloadEditorArgs = {
  value: string
  onChange: (value: string) => void
  editable: boolean
  jsonSchema?: Record<string, unknown>
  updateField: (value: DraftFieldValue) => void
}

/**
 * Lets a specific kind (e.g. Form) reuse the asset edit page while swapping the raw-text payload editor for a bespoke
 * widget and plugging extra read/write behavior into the existing submit flow. All fields are optional; omitting the
 * config yields the generic raw-payload asset behavior.
 */
export type AssetPayloadConfig = {
  renderPayloadEditor?: (args: RenderPayloadEditorArgs) => ReactNode
  // Fallback used only when the entity has no asset.resource link yet; returns the initial payload to seed the editor.
  seedContent?: () => Promise<string | undefined>
  // ANDed into the save-enabled check (e.g. form-level validation errors).
  extraValid?: boolean
  // Throwing pre-write gate run after the entity is created/modified.
  validatePayload?: (payload: string) => void
  // Extra links written in a follow-up patch after create/modify (e.g. form-service contentId/hash).
  buildExtraLinks?: (args: { entityId: EntityId; payload: string }) => Promise<Link[]>
  payloadExtension?: string
  payloadLabel?: string
  payloadMimeType?: string
  payloadLanguage?: Language
  buildPayloadLinks?: (args: { text: string }) => Promise<Link[]>
  renderPreview?: () => ReactNode
  hidePreview?: boolean
}

const AssetEditForm = ({
  entity,
  entityId,
  entityType,
  entityTypes,
  loading,
  config,
  initialText,
}: KindEditPageProps & { config?: AssetPayloadConfig; initialText: string }) => {
  const {
    fields,
    updateField,
    submitting,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerValidation,
    registerPreSubmit,
    registerPostSubmit,
  } = useEntityEdit({ entity, entityId, entityType })

  const { upload } = useUploadResource()
  const modifyMutation = useModifyEntity()

  // Config is rebuilt by wrappers on each render; a ref keeps the submit callbacks stable without re-registering.
  const configRef = useRef(config)
  configRef.current = config

  const existingResourceKey = entity ? getResourceKeyFromEntity(entity) : undefined

  const existingRuntimeRef = entity ? getRuntimeRefFromEntity(entity) : undefined

  const [selectedRuntimeRef, setSelectedRuntimeRef] = useState<RuntimeRef | undefined>()
  const [runtimeInitialized, setRuntimeInitialized] = useState(false)

  if (existingRuntimeRef && !runtimeInitialized) {
    setSelectedRuntimeRef(existingRuntimeRef)
    setRuntimeInitialized(true)
  }

  const selectedRuntimeId = selectedRuntimeRef ? entityIdFromString(selectedRuntimeRef.entityId) : undefined
  const { jsonSchema, documentation } = useRuntimeMetadata(selectedRuntimeId, getKnownKindKey(entityType.kind))

  // The payload is resolved before this component mounts (see AssetEditPage), so the edit state seeds straight from it.
  const [text, setText] = useState<string>(initialText)
  const textRef = useRef<string>(initialText)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const { entries: logEntries, addEntry: addLogEntry, clear: clearLogEntries } = useLogEntries()

  const handleTextChange = (value: string) => {
    setText(value)
    textRef.current = value
  }

  const preSubmit = useCallback(async () => {
    if (configRef.current?.buildPayloadLinks) {
      return configRef.current.buildPayloadLinks({ text: textRef.current })
    }

    const content = textRef.current
    const links = []

    if (content || existingResourceKey) {
      try {
        const blob = new Blob([content], { type: 'application/octet-stream' })
        const resourceKey = await upload({ data: blob, extension: configRef.current?.payloadExtension ?? 'data' })
        links.push(getEntityLinkFromResourceKey(resourceKey))
      } catch (e) {
        console.error(e)
        toast.negative('Failed to upload payload')
        throw e
      }
    }

    // Null link values are only valid for updates (to clear a link), not creates

    if (selectedRuntimeRef) {
      links.push(getEntityLinkFromRuntimeRef(selectedRuntimeRef))
    } else if (entityId) {
      links.push(getEntityLinkFromRuntimeRef(null))
    }

    return links
  }, [upload, existingResourceKey, selectedRuntimeRef, entityId])

  useEffect(() => {
    registerPreSubmit(preSubmit)
  }, [registerPreSubmit, preSubmit])

  // Gate the payload before anything is persisted; validating in postSubmit would run after the asset.resource is
  // already written, leaving a half-saved entity. Runs in both editor views since it reads the shared payload string.
  useEffect(() => {
    registerValidation(() => {
      const validatePayload = configRef.current?.validatePayload
      if (!validatePayload) return true
      try {
        validatePayload(textRef.current)
        return true
      } catch (e) {
        toast.negative(e instanceof Error ? e.message : 'Invalid payload')
        return false
      }
    })
  }, [registerValidation])

  useEffect(() => {
    registerPostSubmit(async (savedEntityId: EntityId) => {
      const cfg = configRef.current
      const payload = textRef.current
      const extraLinks = cfg?.buildExtraLinks ? await cfg.buildExtraLinks({ entityId: savedEntityId, payload }) : []
      if (extraLinks.length === 0) return
      await modifyMutation.mutateAsync({
        id: savedEntityId,
        patch: {
          valuesToUpdateByFieldId: draftFieldValuesToFieldValues([]),
          linksToUpdate: new Map(extraLinks.map((link) => [link.key, link.value])),
          externalValuesToUpdate: new Map(),
        },
      })
    })
  }, [registerPostSubmit, modifyMutation])

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const [previewEntity, setPreviewEntity] = useState<EntityWithLabelAndRelated | undefined>()

  const buildPreviewEntity = useCallback((): EntityWithLabelAndRelated | undefined => {
    const runtimeLinkValue = formatRuntimeRef(selectedRuntimeRef)
    const base = entity?.entity
    const hasLocalRuntime = useLocalRuntimes && !!getKnownKindKey(entityType.kind)

    if (!runtimeLinkValue && !hasLocalRuntime) return undefined

    const linksMap = base ? new Map(base.links.asJsReadonlyMapView()) : new Map<typeof assetRuntimeLinkKey, string>()
    if (runtimeLinkValue) linksMap.set(assetRuntimeLinkKey, runtimeLinkValue)
    linksMap.set(assetResourceLinkKey, 'Blob:' + encodeURIComponent(textRef.current))

    const modified = new Entity(
      base?.id ?? entityIdFromString(crypto.randomUUID()),
      base?.typeId ?? entityType.id,
      base?.valuesByFieldId ?? KtMap.fromJsMap(new Map()),
      KtMap.fromJsMap(linksMap),
      base?.externalValues ?? KtMap.fromJsMap(new Map())
    )
    return new EntityWithLabelAndRelated(
      modified,
      entity?.primaryLabelText ?? '',
      entity?.secondaryLabelText,
      entity?.referencedEntities ?? [],
      []
    )
  }, [entity, selectedRuntimeRef, entityType])

  useEffect(() => {
    if (!entity) return
    // An existing asset with no payload source has nothing to render from the in-memory editor, so preview the saved
    // entity directly (it renders through its own runtime). With a payload source we preview the editable payload.
    const hasPayloadSource = !!existingResourceKey || !!config?.seedContent
    setPreviewEntity(hasPayloadSource ? buildPreviewEntity() : entity)
  }, [entity])

  const label = computeLabel(entityType, fields)

  const inputPanel = (
    <Stack gap="md" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <Group justify="space-between" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
        <Group gap="xxs" align="center" wrap="nowrap">
          <Title order={5}>{config?.payloadLabel ?? 'Payload'}</Title>
          {config?.payloadMimeType && <Chip size="sm">{config.payloadMimeType}</Chip>}
          <PayloadDocumentationButton documentation={documentation} />
        </Group>
        {config?.renderPreview || config?.hidePreview ? null : useLocalRuntimes && getKnownKindKey(entityType.kind) ? (
          <Text size="xs" c="var(--trellis-color-warning-on-background)">
            ⚠️ Local Runtime
          </Text>
        ) : (
          <RuntimeSelector entityTypes={entityTypes} value={selectedRuntimeRef} onChange={setSelectedRuntimeRef} />
        )}
      </Group>
      {config?.renderPayloadEditor ? (
        config.renderPayloadEditor({
          value: text,
          onChange: handleTextChange,
          editable: true,
          jsonSchema,
          updateField,
        })
      ) : (
        <PayloadEditor
          value={text}
          onChange={handleTextChange}
          editable={true}
          placeholder="Enter payload..."
          jsonSchema={jsonSchema}
          defaultLanguage={config?.payloadLanguage}
        />
      )}
    </Stack>
  )

  return (
    <PageLayout
      title={label ?? entityType.name}
      subtitle={label ? entityType.name : undefined}
      showCancel
      showSave
      showDelete={!!entity}
      saveDisabled={!(valid && (config?.extraValid ?? true))}
      submitting={submitting}
      onSave={submitEntity}
      onDelete={deleteEntity}
    >
      <Stack style={{ flex: 1, minHeight: 0 }}>
        {/* New assets need their fields (name etc.) filled in before the payload matters. */}
        <PersistentTabs<AssetEditTab>
          defaultValue={entity ? 'payload' : 'fields'}
          style={{ marginBottom: 8, flexShrink: 0 }}
        >
          <PersistentTab<AssetEditTab> label={config?.payloadLabel ?? 'Payload'} value="payload">
            {config?.hidePreview ? (
              inputPanel
            ) : (
              <ResizableSplit
                autoSaveId="asset-edit-split"
                expandLabel="Show preview"
                left={inputPanel}
                right={({ collapse }) => (
                  <Stack style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                    <Group justify="space-between" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Title order={5}>Preview</Title>
                      <Tooltip label="Hide preview">
                        <IconButton aria-label="hide preview" size="sm" onClick={collapse}>
                          <IconChevronRight size={18} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                    </Group>
                    {config?.renderPreview ? (
                      config.renderPreview()
                    ) : previewEntity ? (
                      <AssetPreviewView
                        entity={previewEntity}
                        entityKind={getKnownKindKey(entityType.kind)}
                        entityTypes={entityTypes}
                        isEditorPreview={previewEntity !== entity}
                        onValidationChange={(result) => setValidationErrors(result.valid ? [] : result.errors)}
                        onRefresh={() => {
                          clearLogEntries()
                          setPreviewEntity(buildPreviewEntity())
                        }}
                        onLog={addLogEntry}
                      />
                    ) : (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {(!selectedRuntimeRef && !(useLocalRuntimes && getKnownKindKey(entityType.kind))) || !text ? (
                          <Text c="dimmed">Select a runtime and add a payload to preview</Text>
                        ) : (
                          <Button variant="outline" onClick={() => setPreviewEntity(buildPreviewEntity())}>
                            Run Preview
                          </Button>
                        )}
                      </div>
                    )}
                    {validationErrors.map((error) => (
                      <Text key={error} size="xs" c="negative">
                        {error}
                      </Text>
                    ))}
                    {previewEntity && <LogPanel entries={logEntries} onClear={clearLogEntries} />}
                  </Stack>
                )}
              />
            )}
          </PersistentTab>
          <PersistentTab<AssetEditTab> label="Fields" value="fields">
            <ScrollFadeContainer>
              <EntityFieldInputGroup
                entityType={entityType}
                entityTypes={entityTypes}
                fields={fields}
                updateField={updateField}
                loading={loading}
              />
            </ScrollFadeContainer>
          </PersistentTab>
        </PersistentTabs>
      </Stack>
    </PageLayout>
  )
}

/**
 * Resolves the asset's initial payload, then mounts the editor with it. Splitting the fetch out (rather than seeding
 * editable state in an effect) keeps the edit state a plain useState initializer and avoids the StrictMode-sensitive
 * "seed once" bookkeeping that previously left legacy (form-service) content unseeded.
 */
const AssetEditPage = (props: KindEditPageProps & { config?: AssetPayloadConfig }) => {
  const { entity, entityType, config } = props
  const existingResourceKey = entity ? getResourceKeyFromEntity(entity) : undefined
  const initialText = useInitialPayload({
    seedQueryKey: entity?.entity.id.toString() ?? 'new',
    existingResourceKey,
    seedContent: config?.seedContent,
  })

  if (initialText === undefined) {
    return (
      <PageLayout title={entityType.name} showCancel>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader />
        </div>
      </PageLayout>
    )
  }

  return <AssetEditForm {...props} initialText={initialText} />
}

export default AssetEditPage
